import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../context/CookieConsentContext';

interface FacebookPixelFunction {
  (...args: any[]): void;
  callMethod?: (...args: any[]) => void;
  queue?: any[];
  push?: (...args: any[]) => void;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: FacebookPixelFunction;
    _fbq?: FacebookPixelFunction;
  }
}

const PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID;

// Initialize Facebook Pixel only when marketing consent is given
const initializeFBPixel = () => {
  if (!PIXEL_ID) return;

  // Properly typed Facebook Pixel initialization
  (function(f: Window, b: Document, e: string, v: string, n: any, t: any, s: any) {
    if (typeof f.fbq === 'function') return;
    n = f.fbq = function() {
      n.callMethod ? 
        n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    } as FacebookPixelFunction;
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    if (s?.parentNode) {
      s.parentNode.insertBefore(t, s);
    }
  }(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
    undefined,
    undefined,
    undefined
  ));
  
  window.fbq?.('init', PIXEL_ID);
};

// Generate a unique event ID for CAPI dedup
export const generateEventId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Get Meta cookies for CAPI user matching
export const getMetaCookies = (): { fbp?: string; fbc?: string } => {
  const cookies = document.cookie.split('; ');
  const fbp = cookies.find(c => c.startsWith('_fbp='))?.split('=')[1];
  const fbc = cookies.find(c => c.startsWith('_fbc='))?.split('=')[1];
  return { fbp, fbc };
};

// Utility functions for tracking events (all include eventID for CAPI dedup)
export const trackAddToCart = (data: {
  content_name: string;
  content_ids: string[];
  value: number;
  currency: string;
}, hasMarketingConsent: boolean = false, eventId?: string) => {
  if (typeof window.fbq === 'function' && hasMarketingConsent) {
    window.fbq('track', 'AddToCart', data, { eventID: eventId || generateEventId() });
  }
};

export const trackViewContent = (data: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
}, hasMarketingConsent: boolean = false, eventId?: string) => {
  if (typeof window.fbq === 'function' && hasMarketingConsent) {
    window.fbq('track', 'ViewContent', data, { eventID: eventId || generateEventId() });
  }
};

export const trackInitiateCheckout = (data: {
  content_ids: string[];
  contents: Array<{ id: string; quantity: number }>;
  value: number;
  currency: string;
}, hasMarketingConsent: boolean = false, eventId?: string) => {
  if (typeof window.fbq === 'function' && hasMarketingConsent) {
    window.fbq('track', 'InitiateCheckout', data, { eventID: eventId || generateEventId() });
  }
};

export const trackPurchase = (data: {
  content_ids: string[];
  contents: Array<{ id: string; quantity: number }>;
  value: number;
  currency: string;
}, hasMarketingConsent: boolean = false, eventId?: string) => {
  if (typeof window.fbq === 'function' && hasMarketingConsent) {
    window.fbq('track', 'Purchase', data, { eventID: eventId || generateEventId() });
  }
};

const MetaPixel: React.FC = () => {
  const { consent } = useCookieConsent();
  const location = useLocation();

  useEffect(() => {
    // Only initialize pixel if marketing consent is given
    if (consent.marketing) {
      initializeFBPixel();
    }
  }, [consent.marketing]);

  useEffect(() => {
    // Only track pageviews if marketing consent is given
    if (consent.marketing && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location, consent.marketing]);

  return null;
};

export default MetaPixel;
